// src/components/admin/pagos/planEditor/ParcialidadesSection.js
import React, { useState } from 'react'
import { Plus, Trash2, Zap, Scale } from 'lucide-react'
import {
  Button, Input, Label, Combobox,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../ui'
import { generarParcialidades, validarParcialidades } from '../../../../lib/services/pagosPlanHelpers'

const r2 = (n) => Math.round(Number(n || 0) * 100) / 100

// El cuadre y el total viven en el ENCABEZADO fijo del wizard (siempre visible),
// así que aquí no se repiten. Las parcialidades reparten el TOTAL completo (que ya
// incluye los uniformes vía su concepto): no hay parcialidades de uniforme aparte.
const ParcialidadesSection = ({ parcialidades = [], total = 0, conceptoOptions = [], onChange }) => {
  const [gen, setGen] = useState({ num: '', fecha: '', primera: '', redondeo: '0' })
  const [genError, setGenError] = useState('')
  const [showConcepto, setShowConcepto] = useState(false)
  const v = validarParcialidades([{ monto: total }], parcialidades)

  const update = (i, patch) => onChange(parcialidades.map((p, idx) => idx === i ? { ...p, ...patch } : p))
  const add = () => onChange([...parcialidades, { orden: parcialidades.length + 1, nombre: `Pago ${parcialidades.length + 1}`, monto: '', fecha_limite: '', concepto_id: null }])
  const remove = (i) => onChange(parcialidades.filter((_, idx) => idx !== i).map((p, idx) => ({ ...p, orden: idx + 1 })))

  const generar = () => {
    const restante = Math.max(0, Number(total))
    const n = Math.floor(Number(gen.num))
    if (!(restante > 0)) { setGenError('Primero agrega conceptos con monto en el paso anterior.'); return }
    if (!n || n < 1) { setGenError('Indica cuántos pagos quieres.'); return }
    setGenError('')
    const nuevas = generarParcialidades({
      total: restante, numParcialidades: n, fechaInicio: gen.fecha || null,
      primeraMonto: Number(gen.primera || 0), redondeo: Number(gen.redondeo || 0),
    })
    onChange(nuevas.map((p, idx) => ({ ...p, orden: idx + 1 })))
  }

  // Pone la diferencia en el último pago para que cuadre exacto.
  const ajustarParaCuadrar = () => {
    const idx = parcialidades.length - 1
    if (idx < 0) { setGenError('No hay un pago para ajustar.'); return }
    const nuevo = r2(Number(parcialidades[idx].monto || 0) - v.diferencia)
    if (nuevo < 0) { setGenError('El último pago quedaría negativo; ajústalo a mano.'); return }
    setGenError('')
    update(idx, { monto: nuevo })
  }

  const conceptoLabel = (id) => conceptoOptions.find((o) => o.value === id)?.label || '—'

  return (
    <div className="space-y-4">
      {total <= 0 && (
        <p className="rounded-lg border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Primero agrega conceptos con monto en el paso anterior; aquí defines en cuántos pagos se cubren.
        </p>
      )}

      {/* Generador */}
      <div className="space-y-3 rounded-lg border bg-muted/30 p-3 sm:p-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs">¿Cuántos pagos?</Label>
            <Input className="h-9" type="number" min="1" max="24" value={gen.num} onChange={(e) => setGen((p) => ({ ...p, num: e.target.value }))} placeholder="6" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Desde (1er pago)</Label>
            <Input className="h-9" type="date" value={gen.fecha} onChange={(e) => setGen((p) => ({ ...p, fecha: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Primer pago distinto</Label>
            <Input className="h-9" type="number" min="0" step="0.01" value={gen.primera} onChange={(e) => setGen((p) => ({ ...p, primera: e.target.value }))} placeholder="$0" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Redondeo</Label>
            <Select value={gen.redondeo} onValueChange={(val) => setGen((p) => ({ ...p, redondeo: val }))}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Exacto</SelectItem>
                <SelectItem value="1">Sin centavos</SelectItem>
                <SelectItem value="50">A $50</SelectItem>
                <SelectItem value="100">A $100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[11px] leading-snug text-muted-foreground">
            Pagos mensuales desde esa fecha. El <span className="font-medium text-foreground">primer pago</span> puede ser distinto al resto (ej. más grande); con <span className="font-medium text-foreground">redondeo</span>, el último pago ajusta el resto.
          </p>
          <Button type="button" size="sm" className="shrink-0" onClick={generar}>
            <Zap className="mr-1 h-4 w-4" /> Generar pagos
          </Button>
        </div>
        {genError && <p className="text-xs font-medium text-amber-600">{genError}</p>}
      </div>

      {/* Lista de pagos */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pagos</Label>
          <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground" onClick={() => setShowConcepto((s) => !s)}>
            {showConcepto ? 'Ocultar concepto' : 'Vincular a concepto'}
          </Button>
        </div>

        {parcialidades.length === 0 ? (
          <p className="rounded-lg border border-dashed py-5 text-center text-sm text-muted-foreground">
            Genera los pagos arriba o agrégalos uno a uno.
          </p>
        ) : (
          <div className="space-y-2">
            {parcialidades.map((p, i) => (
              <div key={i} className="rounded-lg border p-2.5">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-6 min-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">{i + 1}</span>
                  <Input className="h-9 min-w-0 flex-1" value={p.nombre} onChange={(e) => update(i, { nombre: e.target.value })} placeholder="Nombre del pago" />
                  <Input className="h-9 w-24 shrink-0 sm:w-28" type="number" min="0" step="0.01" value={p.monto} onChange={(e) => update(i, { monto: e.target.value })} placeholder="$0" />
                  <Input className="hidden h-9 w-36 shrink-0 sm:block" type="date" value={p.fecha_limite || ''} onChange={(e) => update(i, { fecha_limite: e.target.value })} />
                  <Button type="button" variant="ghost" size="sm" className="h-9 w-9 shrink-0 p-0 text-muted-foreground hover:text-destructive" onClick={() => remove(i)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="mt-2 sm:hidden">
                  <Label className="text-[10px]">Fecha límite</Label>
                  <Input className="h-9" type="date" value={p.fecha_limite || ''} onChange={(e) => update(i, { fecha_limite: e.target.value })} />
                </div>
                {showConcepto && (
                  <div className="mt-2 pl-8">
                    <Label className="text-[10px]">Concepto (opcional)</Label>
                    <Combobox
                      className="h-9 w-full"
                      options={[{ value: '', label: 'Sin concepto' }, ...conceptoOptions]}
                      value={p.concepto_id || ''}
                      onValueChange={(val) => update(i, { concepto_id: val || null })}
                      searchPlaceholder="Buscar..."
                      placeholder={conceptoLabel(p.concepto_id)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 pt-0.5">
          <Button type="button" variant="outline" size="sm" onClick={add}>
            <Plus className="mr-1 h-4 w-4" /> Agregar pago
          </Button>
          {!v.cuadra && parcialidades.length > 0 && (
            <Button type="button" variant="outline" size="sm" onClick={ajustarParaCuadrar}>
              <Scale className="mr-1 h-4 w-4" /> Ajustar para cuadrar
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ParcialidadesSection
